//#region imports
import React, { useState, useEffect, useMemo } from 'react';

// redux
import { useSelector } from 'react-redux';
import { selectUnassignedGateRelocations } from '_redux/slices/timeline/timeline-data';
import { selectThirtyMinLayout } from "_redux/slices/timeline/timeline-control";
import { selectSelectedTask, selectSelectedTasksList, selectSelectedMultiplePAXTask } from "_redux/slices/timeline/selected-task";
import { selectUsedTheme } from '_redux/slices/timeline/theme';

// custom components
import JobCard from '../job-components/job-card';

// icons
import { MdWarningAmber, MdClose } from "react-icons/md";

// styling
import { Spacing } from "_styles";
import * as TaskColors from "../utils/colors";
import "../job-components/job-card.css";

//#endregion

//#region constants

const URGENT_WAIT_MINS = 5;            // unassigned this long → urgent
const URGENT_DEPARTURE_MINS = 30;      // flight departs within this → urgent
const DEPARTED_GRACE_MINS = 15;        // drop from the alert once the flight is this far past its time
const CLOCK_TICK_MS = 30 * 1000;

const URGENT_COLOR = "#C0392B";

//#endregion

//#region helpers

// created time of the relocation task (createdAt ISO, falls back to Firestore pickUpTime)
const getCreatedMs = (task) => {
    if (task?.createdAt) return new Date(task.createdAt).getTime();
    const seconds = task?.pickUpTime?.seconds ?? task?.pickUpTime?._seconds;
    return typeof seconds === 'number' ? seconds * 1000 : null;
};

// live flight time, kept current by the flight feed on flightInformation.flightTime
const getFlightMs = (task) => {
    const time = task?.flightInformation?.flightTime || task?.flightInformation?.operationInfo?.time;
    const ms = time ? new Date(time).getTime() : NaN;
    return Number.isNaN(ms) ? null : ms;
};

const formatTime = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

//#endregion

const GateRelocationAlert = () => {

    //#region redux
    const relocations = useSelector(selectUnassignedGateRelocations);
    const thirtyMinLayout = useSelector(selectThirtyMinLayout);
    const selectedTask = useSelector(selectSelectedTask);
    const selectedTasksList = useSelector(selectSelectedTasksList);
    const selectedMultiplePAXTask = useSelector(selectSelectedMultiplePAXTask);
    const usedTheme = useSelector(selectUsedTheme);
    //#endregion

    //#region local states
    const [now, setNow] = useState(Date.now());
    const [open, setOpen] = useState(false);
    const [clicked, setClicked] = useState([]);
    //#endregion

    //#region useEffect

    // tick so waiting time and urgency update without a data change
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
        return () => clearInterval(interval);
    }, []);

    //#endregion

    //#region derived

    // relocations still actionable, most urgent (earliest flight) first
    const alerts = useMemo(() => {
        return relocations
            .map((task) => {
                const createdMs = getCreatedMs(task);
                const flightMs = getFlightMs(task);
                const waitingMins = createdMs ? Math.max(0, Math.floor((now - createdMs) / 60000)) : null;
                const minsToFlight = flightMs ? Math.floor((flightMs - now) / 60000) : null;
                const urgent = (waitingMins != null && waitingMins >= URGENT_WAIT_MINS) ||
                    (minsToFlight != null && minsToFlight <= URGENT_DEPARTURE_MINS);
                return { task, flightMs, waitingMins, minsToFlight, urgent };
            })
            .filter(({ minsToFlight }) => minsToFlight == null || minsToFlight > -DEPARTED_GRACE_MINS)
            .sort((a, b) => (a.flightMs ?? Infinity) - (b.flightMs ?? Infinity));
    }, [relocations, now]);

    const anyUrgent = alerts.some((alert) => alert.urgent);

    //#endregion

    if (alerts.length === 0) return null;

    const pillColor = anyUrgent ? URGENT_COLOR : TaskColors.gateRelocationTask;

    return (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', padding: Spacing.SCALE_4 }}>

            {/* alert pill */}
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    backgroundColor: pillColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: 16,
                    padding: '4px 12px',
                    fontWeight: 'bold',
                    fontSize: 13,
                    cursor: 'pointer',
                    animation: anyUrgent ? 'rc-beacon-pulse 1.5s infinite' : 'none',
                }}
            >
                <MdWarningAmber size={18} />
                {alerts.length} Unassigned Gate Relocation{alerts.length > 1 ? 's' : ''}
            </button>

            {/* relocation list */}
            {open && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    zIndex: 1000,
                    width: 380,
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    backgroundColor: usedTheme.BACKGROUND_COLOR,
                    border: `1px solid ${pillColor}`,
                    borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                    padding: Spacing.SCALE_8,
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: Spacing.SCALE_8 }}>
                        <span style={{ fontWeight: 'bold', fontSize: 13, color: usedTheme.TEXT_COLOR }}>
                            Select a relocation, then assign it to an agent
                        </span>
                        <MdClose size={18} style={{ cursor: 'pointer' }} color={usedTheme.TEXT_COLOR} onClick={() => setOpen(false)} />
                    </div>

                    {alerts.map(({ task, flightMs, waitingMins, minsToFlight, urgent }, index) => (
                        <div key={task._id || index} style={{ marginBottom: Spacing.SCALE_8 }}>

                            {/* relocation summary */}
                            <div style={{
                                backgroundColor: urgent ? URGENT_COLOR : TaskColors.gateRelocationTask,
                                color: 'white',
                                fontSize: 12,
                                fontWeight: 'bold',
                                padding: '4px 6px',
                                borderRadius: '4px 4px 0 0',
                            }}>
                                {[
                                    task.flightInformation?.flightNumber,
                                    flightMs && `${minsToFlight < 0 ? 'Departed' : 'Dep'} ${formatTime(flightMs)}`,
                                    `${task.pickUpLocation?.name || '?'} → ${task.dropOffLocation?.name || '?'}`,
                                    waitingMins != null && `unassigned ${waitingMins} min`,
                                ].filter(Boolean).join(' · ')}
                            </div>

                            <JobCard
                                job={task}
                                index={index}
                                timelineJobs={true}
                                clicked={clicked}
                                setClicked={setClicked}
                                thirtyMinLayout={thirtyMinLayout}
                                selectedTask={selectedTask}
                                selectedTasksList={selectedTasksList}
                                usedTheme={usedTheme}
                                selectedMultiplePAXTask={selectedMultiplePAXTask}
                                showFullCard={true}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default React.memo(GateRelocationAlert);
